import type { CSSProperties } from 'react';

import type { FlowMeta } from './aiClient';
import { useModels } from './useModels';

/**
 * Native-<select> dropdown showing the EXACT model slug
 * (`claude-opus-4-7`, `claude-sonnet-4-6`, etc.). Two visual variants
 * for two host UIs:
 *
 *   - "card"   — used in the Generate modal. Labelled section ("Model")
 *                + a wider dropdown.
 *
 *   - "inline" — used in the AI Chat panel header. Compact inline
 *                dropdown.
 *
 * Both variants are the same controlled component: parent owns the
 * picked value, picker calls onChange(slug). Disabled state is
 * parent-controlled (chat panel locks it after a session has started
 * — model is sticky-per-session on the server).
 *
 * We show the raw slug + a "default" marker only — no rate / cost
 * text. Cost is surfaced AFTER a generation in the success line of
 * the Generate modal (where it's actual spend), not as a forecast in
 * the picker (where it would just be noise next to the slug).
 *
 * Raw slug is intentional: friendly labels (e.g. "Opus 4.7") would
 * hide the legacy-vs-current tier distinction (claude-opus-4-7 vs
 * claude-opus-4-20250514 are different pricing tiers). Raw slugs
 * make the picker self-documenting.
 */

type Variant = 'card' | 'inline';
type Flow = 'generate' | 'chat';

export interface ModelPickerProps {
  flow: Flow;
  /** Currently-selected slug. `undefined` means use the server default. */
  value: string | undefined;
  onChange: (slug: string) => void;
  variant?: Variant;
  disabled?: boolean;
  /**
   * Optional message rendered below the picker when disabled. Used
   * in the chat panel to explain "Locked for this session".
   */
  disabledHint?: string;
}

export function ModelPicker({
  flow,
  value,
  onChange,
  variant = 'card',
  disabled = false,
  disabledHint,
}: ModelPickerProps) {
  const { data, isLoading, error } = useModels();
  const meta: FlowMeta | undefined = data?.[flow];
  const effective = value ?? meta?.default;

  if (isLoading) {
    return <div style={s.loading}>Loading models…</div>;
  }
  if (error || !meta) {
    // Surface the failure inline rather than collapsing to nothing.
    // A silent disappearance was the #1 cause of "the picker isn't
    // showing up" debugging — usually because the ai-service
    // container is running stale compiled code that doesn't have
    // the /ai/models route yet. Visible error → instant diagnosis.
    const reason = error
      ? error instanceof Error ? error.message : String(error)
      : 'no models response';
    return (
      <div style={s.errorBox}>
        <strong>Model picker unavailable:</strong> {reason}
        <div style={s.errorHint}>
          Generation will still work using the server's default model.
          If this persists, the ai-service container may need a rebuild
          (the /ai/models route was added recently).
        </div>
      </div>
    );
  }

  const options = meta.allowed.map((m) => ({
    slug: m.slug,
    label: m.slug === meta.default ? `${m.slug}  —  default` : m.slug,
  }));

  if (variant === 'inline') {
    return (
      <div style={s.inlineWrap}>
        <label style={s.inlineLabel} htmlFor={`model-picker-${flow}`}>Model</label>
        <select
          id={`model-picker-${flow}`}
          style={disabled ? s.inlineSelectDisabled : s.inlineSelect}
          value={effective ?? ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        >
          {options.map((opt) => (
            <option key={opt.slug} value={opt.slug}>
              {opt.label}
            </option>
          ))}
        </select>
        {disabled && disabledHint && (
          <span style={s.inlineHint}>{disabledHint}</span>
        )}
      </div>
    );
  }

  return (
    <div style={s.cardSection}>
      <h3 style={s.cardTitle}>Model</h3>
      <select
        style={disabled ? s.cardSelectDisabled : s.cardSelect}
        value={effective ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        {options.map((opt) => (
          <option key={opt.slug} value={opt.slug}>
            {opt.label}
          </option>
        ))}
      </select>
      {disabled && disabledHint && (
        <div style={s.cardDisabledHint}>{disabledHint}</div>
      )}
    </div>
  );
}

const s: Record<string, CSSProperties> = {
  loading: { fontSize: 11, color: '#94A3B8' },
  errorBox: {
    padding: '8px 10px', marginTop: 8, fontSize: 11, color: '#92400E',
    backgroundColor: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 6,
    lineHeight: 1.5,
  },
  errorHint: { marginTop: 4, fontSize: 10, color: '#78350F' },

  /* ----- card variant (Generate modal) ----- */
  cardSection: { marginTop: 16 },
  cardTitle: {
    margin: '0 0 6px', fontSize: 12, fontWeight: 700, color: '#64748B',
    textTransform: 'uppercase' as const, letterSpacing: 0.5,
  },
  cardSelect: {
    width: '100%', padding: '8px 10px', fontSize: 13, color: '#0F172A',
    backgroundColor: '#fff', border: '1px solid #CBD5E1', borderRadius: 6,
    cursor: 'pointer', fontFamily: 'ui-monospace, monospace', boxSizing: 'border-box',
  },
  cardSelectDisabled: {
    width: '100%', padding: '8px 10px', fontSize: 13, color: '#64748B',
    backgroundColor: '#F1F5F9', border: '1px solid #CBD5E1', borderRadius: 6,
    cursor: 'not-allowed', fontFamily: 'ui-monospace, monospace', boxSizing: 'border-box',
  },
  cardDisabledHint: { marginTop: 6, fontSize: 11, color: '#94A3B8', fontStyle: 'italic' as const },

  /* ----- inline variant (Chat panel header) ----- */
  inlineWrap: { display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  inlineLabel: {
    fontSize: 10, fontWeight: 700, color: '#64748B',
    textTransform: 'uppercase' as const, letterSpacing: 0.5,
  },
  inlineSelect: {
    padding: '2px 6px', fontSize: 11, color: '#0F172A',
    backgroundColor: '#fff', border: '1px solid #CBD5E1', borderRadius: 4,
    cursor: 'pointer', fontFamily: 'ui-monospace, monospace', maxWidth: 320,
  },
  inlineSelectDisabled: {
    padding: '2px 6px', fontSize: 11, color: '#64748B',
    backgroundColor: '#F1F5F9', border: '1px solid #CBD5E1', borderRadius: 4,
    cursor: 'not-allowed', fontFamily: 'ui-monospace, monospace', maxWidth: 320,
  },
  inlineHint: { fontSize: 10, color: '#94A3B8' },
};
